import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import StoreMultiSelectFilter from "../components/StoreMultiSelectFilter";
import {
  addPartsToStoreBatch,
  changeOpsPartItemsToJit,
  createStoreBatch,
  getOpsPartItems,
  getStoreBatches,
  getStoreFactories,
  updateOpsPartItem,
} from "../production/service";
import { toErrorText } from "./storeSalesDashboardHelpers";

const PART_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "planning", label: "Planning" },
];

const EMPTY_FILTER_LABEL = "—";

const formatPriorityDate = (value) => {
  const t = Date.parse(value);

  return new Date(t).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getProjectFilterValue = (row) =>
  String(row.order_identifier ?? "").trim() || EMPTY_FILTER_LABEL;

const formatProductionType = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "jit") return "JIT";
  if (normalized === "pm") return "PM";
  return EMPTY_FILTER_LABEL;
};

const getDescriptionFilterValue = (row) =>
  String(row.description ?? "").trim() || EMPTY_FILTER_LABEL;

const getThicknessFilterValue = (row) => String(row.thickness ?? "");

const getPanelNameFilterValue = (row) =>
  String(row.panel_name ?? "").trim() || EMPTY_FILTER_LABEL;

const getRemarkFilterValue = (row) =>
  String(row.remark ?? "").trim() || EMPTY_FILTER_LABEL;

const getPriorityFilterValue = (row) => formatPriorityDate(row.priority);

const uniqueSortedOptions = (values) => {
  const set = new Set();

  values.forEach((value) => {
    const normalized = String(value ?? "").trim();
    set.add(normalized || EMPTY_FILTER_LABEL);
  });

  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
};

const hasActiveColumnFilters = ({
  projectFilter,
  typeFilter,
  descriptionFilter,
  thicknessFilter,
  panelNameFilter,
  remarkFilter,
  priorityFilter,
}) =>
  projectFilter.length > 0 ||
  typeFilter.length > 0 ||
  descriptionFilter.length > 0 ||
  thicknessFilter.length > 0 ||
  panelNameFilter.length > 0 ||
  remarkFilter.length > 0 ||
  priorityFilter.length > 0;

const StoreOpsPartsSection = () => {
  const [statusFilter, setStatusFilter] = useState("draft");
  const [partItems, setPartItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [projectFilter, setProjectFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [descriptionFilter, setDescriptionFilter] = useState([]);
  const [thicknessFilter, setThicknessFilter] = useState([]);
  const [panelNameFilter, setPanelNameFilter] = useState([]);
  const [remarkFilter, setRemarkFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [isFactoryModalOpen, setIsFactoryModalOpen] = useState(false);
  const [isExistingBatchModalOpen, setIsExistingBatchModalOpen] =
    useState(false);
  const [factories, setFactories] = useState([]);
  const [draftBatches, setDraftBatches] = useState([]);
  const [factoryId, setFactoryId] = useState("");
  const [existingBatchId, setExistingBatchId] = useState("");
  const [factoryLoading, setFactoryLoading] = useState(false);
  const [existingBatchLoading, setExistingBatchLoading] = useState(false);
  const [factoryError, setFactoryError] = useState(null);
  const [existingBatchError, setExistingBatchError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPartItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getOpsPartItems();
      setPartItems(rows);
    } catch (err) {
      setPartItems([]);
      setError(toErrorText(err, "Unable to load part items."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPartItems();
  }, [loadPartItems]);

  useEffect(() => {
    setSelectedIds([]);
    setFactoryError(null);
    setProjectFilter([]);
    setTypeFilter([]);
    setDescriptionFilter([]);
    setThicknessFilter([]);
    setPanelNameFilter([]);
    setRemarkFilter([]);
    setPriorityFilter([]);
  }, [statusFilter]);

  const statusFilteredPartItems = useMemo(
    () => partItems.filter((row) => row.status === statusFilter),
    [partItems, statusFilter]
  );

  const projectFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredPartItems.map((row) => getProjectFilterValue(row))
      ),
    [statusFilteredPartItems]
  );

  const typeFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredPartItems.map((row) =>
          formatProductionType(row.production_type)
        )
      ),
    [statusFilteredPartItems]
  );

  const descriptionFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredPartItems.map((row) => getDescriptionFilterValue(row))
      ),
    [statusFilteredPartItems]
  );

  const thicknessFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredPartItems.map((row) => getThicknessFilterValue(row))
      ),
    [statusFilteredPartItems]
  );

  const panelNameFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredPartItems.map((row) => getPanelNameFilterValue(row))
      ),
    [statusFilteredPartItems]
  );

  const remarkFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredPartItems.map((row) => getRemarkFilterValue(row))
      ),
    [statusFilteredPartItems]
  );

  const priorityFilterOptions = useMemo(
    () =>
      uniqueSortedOptions(
        statusFilteredPartItems.map((row) => getPriorityFilterValue(row))
      ),
    [statusFilteredPartItems]
  );

  const displayedPartItems = useMemo(() => {
    return statusFilteredPartItems.filter((row) => {
      if (
        projectFilter.length > 0 &&
        !projectFilter.includes(getProjectFilterValue(row))
      ) {
        return false;
      }

      if (
        typeFilter.length > 0 &&
        !typeFilter.includes(formatProductionType(row.production_type))
      ) {
        return false;
      }

      if (
        descriptionFilter.length > 0 &&
        !descriptionFilter.includes(getDescriptionFilterValue(row))
      ) {
        return false;
      }

      if (
        thicknessFilter.length > 0 &&
        !thicknessFilter.includes(getThicknessFilterValue(row))
      ) {
        return false;
      }

      if (
        panelNameFilter.length > 0 &&
        !panelNameFilter.includes(getPanelNameFilterValue(row))
      ) {
        return false;
      }

      if (
        remarkFilter.length > 0 &&
        !remarkFilter.includes(getRemarkFilterValue(row))
      ) {
        return false;
      }

      if (
        priorityFilter.length > 0 &&
        !priorityFilter.includes(getPriorityFilterValue(row))
      ) {
        return false;
      }

      return true;
    });
  }, [
    statusFilteredPartItems,
    projectFilter,
    typeFilter,
    descriptionFilter,
    thicknessFilter,
    panelNameFilter,
    remarkFilter,
    priorityFilter,
  ]);

  useEffect(() => {
    const visibleIds = new Set(
      displayedPartItems.map((row) => row.id).filter((id) => id != null)
    );

    setSelectedIds((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [displayedPartItems]);

  const columnFiltersActive = hasActiveColumnFilters({
    projectFilter,
    typeFilter,
    descriptionFilter,
    thicknessFilter,
    panelNameFilter,
    remarkFilter,
    priorityFilter,
  });

  const clearColumnFilters = () => {
    setProjectFilter([]);
    setTypeFilter([]);
    setDescriptionFilter([]);
    setThicknessFilter([]);
    setPanelNameFilter([]);
    setRemarkFilter([]);
    setPriorityFilter([]);
  };

  const showSelectionColumn =
    statusFilter === "draft" || statusFilter === "planning";

  const pillClassName = useMemo(
    () => (value) =>
      `btn btn-sm rounded-pill ${
        statusFilter === value ? "btn-primary" : "btn-light border text-dark"
      }`,
    [statusFilter]
  );

  const visibleIds = useMemo(
    () => displayedPartItems.map((row) => row.id).filter((id) => id != null),
    [displayedPartItems]
  );

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const selectedSqftTotal = useMemo(() => {
    if (selectedIds.length === 0) return null;

    return displayedPartItems
      .filter((row) => selectedIds.includes(row.id))
      .reduce((sum, row) => sum + Number(row.sqft), 0);
  }, [displayedPartItems, selectedIds]);

  const allSelectedAreJit = useMemo(() => {
    if (selectedIds.length === 0) return false;

    return displayedPartItems
      .filter((row) => selectedIds.includes(row.id))
      .every((row) => row.production_type === "jit");
  }, [displayedPartItems, selectedIds]);

  const selectedDraftPmIds = useMemo(
    () =>
      displayedPartItems
        .filter(
          (row) =>
            selectedIds.includes(row.id) &&
            row.production_type === "pm" &&
            row.status === "draft"
        )
        .map((row) => row.id)
        .filter((id) => id != null),
    [displayedPartItems, selectedIds]
  );

  const anySelectedIsJit = useMemo(() => {
    if (selectedIds.length === 0) return false;

    return displayedPartItems
      .filter((row) => selectedIds.includes(row.id))
      .some((row) => row.production_type === "jit");
  }, [displayedPartItems, selectedIds]);

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const toggleAllVisible = () => {
    setSelectedIds(allVisibleSelected ? [] : visibleIds);
  };

  const runAction = async (action) => {
    setActionLoading(true);
    setError(null);
    try {
      await action();
      setSelectedIds([]);
      await loadPartItems();
    } catch (err) {
      setError(toErrorText(err, "Unable to update part items."));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmAndRun = (message, action) => {
    if (!window.confirm(message)) return;
    runAction(action);
  };

  const handleMoveToPlanning = () =>
    confirmAndRun(`Move ${selectedIds.length} part item(s) to Planning?`, () =>
      Promise.all(selectedIds.map((id) => updateOpsPartItem(id, "planning")))
    );

  const handleChangeToJit = () =>
    confirmAndRun(
      `Change ${selectedDraftPmIds.length} part item(s) from PM to JIT?`,
      () => changeOpsPartItemsToJit(selectedDraftPmIds)
    );

  const handleMoveToDraft = () =>
    confirmAndRun(
      `Move ${selectedIds.length} part item(s) back to Draft?`,
      () => Promise.all(selectedIds.map((id) => updateOpsPartItem(id, "draft")))
    );

  const loadFactories = useCallback(async () => {
    setFactoryLoading(true);
    setFactoryError(null);
    try {
      const rows = await getStoreFactories();
      setFactories(rows);
    } catch (err) {
      setFactories([]);
      setFactoryError(toErrorText(err, "Unable to load factories."));
    } finally {
      setFactoryLoading(false);
    }
  }, []);

  const loadDraftBatches = useCallback(async () => {
    setExistingBatchLoading(true);
    setExistingBatchError(null);
    try {
      const rows = await getStoreBatches({ status: "draft" });
      setDraftBatches(rows);
    } catch (err) {
      setDraftBatches([]);
      setExistingBatchError(toErrorText(err, "Unable to load batches."));
    } finally {
      setExistingBatchLoading(false);
    }
  }, []);

  const openFactoryModal = () => {
    setFactoryId("");
    setFactoryError(null);
    setIsFactoryModalOpen(true);
    loadFactories();
  };

  const closeFactoryModal = () => {
    if (actionLoading) return;
    setIsFactoryModalOpen(false);
    setFactoryId("");
    setFactoryError(null);
  };

  const openExistingBatchModal = () => {
    setExistingBatchId("");
    setExistingBatchError(null);
    setIsExistingBatchModalOpen(true);
    loadDraftBatches();
  };

  const closeExistingBatchModal = () => {
    if (actionLoading) return;
    setIsExistingBatchModalOpen(false);
    setExistingBatchId("");
    setExistingBatchError(null);
  };

  const handleCreateBatch = () => {
    runAction(async () => {
      await createStoreBatch({
        factory_id: Number(factoryId),
        part_item_ids: selectedIds,
      });
      closeFactoryModal();
    });
  };

  const handleAddToExistingBatch = () => {
    runAction(async () => {
      await addPartsToStoreBatch(Number(existingBatchId), {
        part_item_ids: selectedIds,
      });
      closeExistingBatchModal();
    });
  };

  const renderActions = () => {
    if (statusFilter === "draft") {
      return (
        <div className="d-flex flex-wrap gap-2">
          <Button
            color="primary"
            size="sm"
            disabled={!allSelectedAreJit || actionLoading}
            onClick={handleMoveToPlanning}
          >
            Move to Planning
          </Button>
          <Button
            color="primary"
            outline
            size="sm"
            disabled={
              anySelectedIsJit ||
              selectedDraftPmIds.length === 0 ||
              actionLoading
            }
            onClick={handleChangeToJit}
          >
            Change to JIT
          </Button>
        </div>
      );
    }

    if (statusFilter === "planning") {
      return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100">
          <div className="d-flex flex-wrap gap-2">
            <Button
              color="secondary"
              size="sm"
              disabled={selectedIds.length === 0 || actionLoading}
              onClick={handleMoveToDraft}
            >
              Move back to Draft
            </Button>
            <Button
              color="primary"
              size="sm"
              disabled={selectedIds.length === 0 || actionLoading}
              onClick={openFactoryModal}
            >
              Create new Batch
            </Button>
            <Button
              color="primary"
              outline
              size="sm"
              disabled={selectedIds.length === 0 || actionLoading}
              onClick={openExistingBatchModal}
            >
              Add to Existing Batch
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  const actions = renderActions();
  const columnCount = showSelectionColumn ? 11 : 10;
  const emptyMessage =
    statusFilteredPartItems.length === 0
      ? "No part items found."
      : "No part items match filters.";

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="text-muted small me-2">Status:</span>
          {PART_STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={pillClassName(option.value)}
              onClick={() => setStatusFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {selectedSqftTotal != null && (
          <span className="text-muted small d-flex flex-wrap gap-3">
            <span>Total sqft: {selectedSqftTotal.toFixed(2)}</span>
            <span>
              Approx. Boards: {(selectedSqftTotal / 0.95 / 32).toFixed(2)}
            </span>
          </span>
        )}
      </div>

      {actions && <div className="mb-3">{actions}</div>}

      {!loading && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <StoreMultiSelectFilter
            label="Project"
            options={projectFilterOptions}
            value={projectFilter}
            onChange={setProjectFilter}
            minWidth="140px"
          />
          <StoreMultiSelectFilter
            label="Type"
            options={typeFilterOptions}
            value={typeFilter}
            onChange={setTypeFilter}
            minWidth="120px"
          />
          <StoreMultiSelectFilter
            label="Description"
            options={descriptionFilterOptions}
            value={descriptionFilter}
            onChange={setDescriptionFilter}
            minWidth="180px"
          />
          <StoreMultiSelectFilter
            label="Thickness"
            options={thicknessFilterOptions}
            value={thicknessFilter}
            onChange={setThicknessFilter}
            minWidth="140px"
          />
          <StoreMultiSelectFilter
            label="Panel Name"
            options={panelNameFilterOptions}
            value={panelNameFilter}
            onChange={setPanelNameFilter}
            minWidth="160px"
          />
          <StoreMultiSelectFilter
            label="Remark"
            options={remarkFilterOptions}
            value={remarkFilter}
            onChange={setRemarkFilter}
            minWidth="160px"
          />
          <StoreMultiSelectFilter
            label="Priority"
            options={priorityFilterOptions}
            value={priorityFilter}
            onChange={setPriorityFilter}
            minWidth="140px"
          />
          {columnFiltersActive && (
            <Button
              color="light"
              size="sm"
              className="border text-dark"
              onClick={clearColumnFilters}
            >
              Clear filters
            </Button>
          )}
        </div>
      )}

      {error && (
        <Alert color="danger" fade={false}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="py-5 text-center text-muted">
          <Spinner className="me-2" />
          Loading…
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover responsive className="mb-0 store-sales-table">
            <thead className="store-sales-table__head">
              <tr>
                {showSelectionColumn && (
                  <th style={{ width: "3rem" }}>
                    <Input
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={visibleIds.length === 0 || actionLoading}
                      onChange={toggleAllVisible}
                      aria-label="Select all visible part items"
                    />
                  </th>
                )}
                <th>Project</th>
                <th>Type</th>
                <th>Description</th>
                <th>Thickness</th>
                <th>FB Length</th>
                <th>FB Width</th>
                <th>Panel Name</th>
                <th>Remark</th>
                <th>Assembly Label</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {displayedPartItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="text-muted text-center py-4"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                displayedPartItems.map((row) => (
                  <tr key={row.id}>
                    {showSelectionColumn && (
                      <td>
                        <Input
                          type="checkbox"
                          checked={selectedIds.includes(row.id)}
                          disabled={actionLoading}
                          onChange={() => toggleRow(row.id)}
                          aria-label={`Select part item ${row.id}`}
                        />
                      </td>
                    )}
                    <td>{row.order_identifier}</td>
                    <td>{formatProductionType(row.production_type)}</td>
                    <td>{row.description}</td>
                    <td>{row.thickness}</td>
                    <td>{row.fb_length}</td>
                    <td>{row.fb_width}</td>
                    <td>{row.panel_name}</td>
                    <td>{row.remark || "-"}</td>
                    <td>{row.assembly_label}</td>
                    <td>{formatPriorityDate(row.priority)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
      <Modal isOpen={isFactoryModalOpen} toggle={closeFactoryModal} centered>
        <ModalHeader toggle={closeFactoryModal}>Create new Batch</ModalHeader>
        <ModalBody>
          {factoryError && (
            <Alert color="danger" fade={false}>
              {factoryError}
            </Alert>
          )}
          <label htmlFor="ops-factory-select" className="form-label mb-2">
            Factory
          </label>
          <Input
            id="ops-factory-select"
            type="select"
            value={factoryId}
            disabled={factoryLoading || actionLoading}
            onChange={(event) => setFactoryId(event.target.value)}
          >
            <option value="">Select a factory</option>
            {factories.map((factory) => (
              <option key={factory.id} value={factory.id}>
                {factory.name}
              </option>
            ))}
          </Input>
          {factoryLoading && (
            <div className="mt-3 text-muted small">
              <Spinner size="sm" className="me-2" />
              Loading factories...
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={closeFactoryModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleCreateBatch}
            disabled={!factoryId || actionLoading || factoryLoading}
          >
            Submit
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={isExistingBatchModalOpen}
        toggle={closeExistingBatchModal}
        centered
      >
        <ModalHeader toggle={closeExistingBatchModal}>
          Add to Existing Batch
        </ModalHeader>
        <ModalBody>
          {existingBatchError && (
            <Alert color="danger" fade={false}>
              {existingBatchError}
            </Alert>
          )}
          {existingBatchLoading ? (
            <div className="text-muted small">
              <Spinner size="sm" className="me-2" />
              Loading batches...
            </div>
          ) : draftBatches.length === 0 ? (
            <Alert color="warning" fade={false} className="mb-0">
              No draft batches available.
            </Alert>
          ) : (
            <>
              <label
                htmlFor="ops-existing-batch-select"
                className="form-label mb-2"
              >
                Batch
              </label>
              <Input
                id="ops-existing-batch-select"
                type="select"
                value={existingBatchId}
                disabled={actionLoading}
                onChange={(event) => setExistingBatchId(event.target.value)}
              >
                <option value="">Select a batch</option>
                {draftBatches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name} — {batch.factory.name}
                  </option>
                ))}
              </Input>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={closeExistingBatchModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleAddToExistingBatch}
            disabled={
              !existingBatchId ||
              actionLoading ||
              existingBatchLoading ||
              draftBatches.length === 0
            }
          >
            Submit
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StoreOpsPartsSection;
